import { useState } from 'react';
import { TreeProps, ExpandedNodesMap, Node, SelectedNodesMap } from '../';
import { useGetFlatTreeNodeList } from './useGetFlatTreeNodeList';

const getVisibleNodes = (nodes: Node[], expandedNodes: ExpandedNodesMap): string[] => {
  const aVisibleNodes: string[] = [];
  for (const node of nodes) {
    aVisibleNodes.push(node.id);
    if (expandedNodes[node.id] && node.children && node.children.length > 0) {
      aVisibleNodes.push(...getVisibleNodes(node.children, expandedNodes));
    }
  }
  return aVisibleNodes;
};

export const useTreeState = (
  initialExpandedNodes: ExpandedNodesMap,
  nodes: Node[],
  initialSelectedNodes?: SelectedNodesMap,
  selectionMode?: 'single' | 'desktop' | 'none' | 'singleLeaf'
) => {
  const [expandedNodes, setExpandedNodes] = useState<ExpandedNodesMap>(initialExpandedNodes);
  const [selectedNodes, setSelectedNodes] = useState<SelectedNodesMap>(initialSelectedNodes ?? {});
  const [currentSelectedNode, setCurrentSelectedNode] = useState<string>('');

  const onExpand: TreeProps['onExpand'] = (node: Node, expanded: boolean) => {
    if (expanded) {
      setExpandedNodes({ ...expandedNodes, [node.id]: true });
    } else {
      const { [node.id]: isExpanded, ...restOfExpandedNodes } = expandedNodes;
      setExpandedNodes(restOfExpandedNodes);
    }
  };

  const onSelect: TreeProps['onSelect'] = (
    node: Node,
    eventData?: { keyPressed?: string; ctrlKey: boolean; shiftKey: boolean; metaKey: boolean }
  ) => {
    selectionMode = selectionMode ?? 'single';
    let selectionType: 'select' | 'toggle' | 'range' = 'select';
    if (eventData && selectionMode === 'desktop') {
      if (eventData.ctrlKey) {
        selectionType = 'toggle';
      } else if (eventData.shiftKey) {
        selectionType = 'range';
      }
      if (eventData.ctrlKey && eventData.keyPressed === ' ') {
        // toggle select state if either space bar or spacebar and ctrl key is pressed
        selectionType = 'toggle';
      }
    }

    switch (selectionMode) {
      case 'single':
        setSelectedNodes({ [node.id]: true });
        setCurrentSelectedNode(node.id);
        break;

      case 'desktop':
        if (selectionType === 'toggle') {
          // toggle the select state of node and keep other selected nodes as it is
          const { [node.id]: isSelected, ...restOfSelectedNodes } = selectedNodes;
          if (isSelected) {
            setSelectedNodes(restOfSelectedNodes);
          } else {
            setSelectedNodes({ ...selectedNodes, [node.id]: true });
            setCurrentSelectedNode(node.id);
          }
        } else if (selectionType === 'range') {
          // select the range of nodes starting from the last selected node
          const aSelectableNodes = getVisibleNodes(nodes, expandedNodes);
          let iStart = aSelectableNodes?.indexOf(node.id);
          let iEnd = aSelectableNodes?.indexOf(currentSelectedNode);

          if (iStart > iEnd) {
            [iStart, iEnd] = [iEnd, iStart];
          }
          let selectNodes = {};
          for (let i = iStart; i <= iEnd; i++) {
            selectNodes = { ...selectNodes, [aSelectableNodes[i]]: true };
          }
          setSelectedNodes(selectNodes);
          setCurrentSelectedNode(node.id);
        } else {
          if (eventData && eventData.keyPressed === ' ') {
            // toggle node selection and deselect all other selected nodes if spacebar key is pressed
            const { [node.id]: isSelected } = selectedNodes;
            if (isSelected) {
              setSelectedNodes({});
            } else {
              setSelectedNodes({ [node.id]: true });
            }
          } else {
            // select node and deselect all other selected nodes
            setSelectedNodes({ [node.id]: true });
            setCurrentSelectedNode(node.id);
          }
        }
        break;

      case 'singleLeaf':
        if (!node.children) {
          setSelectedNodes({ [node.id]: true });
          setCurrentSelectedNode(node.id);
        }
        break;
      case 'none':
        break;
    }
  };

  const fetchChildren = (nodes: Node[], children: string[]): void => {
    for (let i = 0; i < nodes.length; i++) {
      children.push(nodes[i].id);
      if (nodes[i].children !== undefined && nodes[i].children!.length > 0) {
        fetchChildren(nodes[i].children!, children);
      }
    }
  };
  const onExpandAll: TreeProps['onExpandAll'] = () => {
    if (!nodes) return;
    let expandedNodesId: string[] = [];
    const expandedNodes: ExpandedNodesMap = {};
    for (let i = 0; i < nodes.length; i++) {
      if (nodes[i].children !== undefined && nodes[i].children!.length > 0) {
        const children: string[] = [];
        fetchChildren(nodes, children);
        expandedNodesId = expandedNodesId.concat(children);
      }
    }
    expandedNodesId.forEach((id) => {
      expandedNodes[id] = true;
    });
    setExpandedNodes(expandedNodes);
  };
  const onCollapseAll: TreeProps['onCollapseAll'] = () => {
    setExpandedNodes({});
  };

  const flattenedNodes = useGetFlatTreeNodeList(nodes, expandedNodes);

  return {
    nodes: flattenedNodes,
    expandedNodes,
    setExpandedNodes,
    onExpand,
    selectedNodes,
    setSelectedNodes,
    selectionMode,
    onSelect,
    onExpandAll,
    onCollapseAll,
  };
};
